import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Trash2, FileText, Download, Eye, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import type {
  DeliveryNote,
  DeliveryNoteItem,
  DeliveryNoteCustomer,
} from '@/types/deliveryNote';
import {
  DEFAULT_COMPANY,
  calculateItemTotals,
  calculateTax,
  formatCurrency,
  generateDocumentNumber,
} from '@/types/deliveryNote';
import { downloadDeliveryNotePdf, openDeliveryNotePdfInNewTab } from '@/lib/deliveryNotePdf';
import { api } from '@/lib/apiClient';

interface DeliveryNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrderId?: string;
  workOrderNumber?: string;
  initialCustomer?: Partial<DeliveryNoteCustomer>;
  initialItems?: Partial<DeliveryNoteItem>[];
  initialDeliveryNote?: DeliveryNote | null;
  initialDeliveryNoteId?: string | null;
  onSaved?: (noteId: string) => void;
  documentSequence?: number;
}

export function DeliveryNoteDialog({
  open,
  onOpenChange,
  workOrderId,
  workOrderNumber,
  initialCustomer,
  initialItems,
  initialDeliveryNote,
  initialDeliveryNoteId,
  onSaved,
  documentSequence = 1,
}: DeliveryNoteDialogProps) {
  const [loading, setLoading] = useState(false);
  const [noteId, setNoteId] = useState<string | null>(null);

  // Customer state
  const [customer, setCustomer] = useState<DeliveryNoteCustomer>({
    name: initialCustomer?.name || '',
    cif: initialCustomer?.cif || '',
    address: initialCustomer?.address || '',
    city: initialCustomer?.city || '',
    province: initialCustomer?.province || '',
    postalCode: initialCustomer?.postalCode || '',
  });

  // Document info state
  const [documentNumber, setDocumentNumber] = useState(
    generateDocumentNumber(documentSequence)
  );
  const [documentDate, setDocumentDate] = useState(
    format(new Date(), 'dd/MM/yyyy')
  );
  const [paymentMethod, setPaymentMethod] = useState('Transferencia bancaria');
  const [hideValues, setHideValues] = useState(false);

  // Items state
  const [items, setItems] = useState<DeliveryNoteItem[]>(
    initialItems?.map((item, index) => ({
      id: `item-${index}`,
      article: item.article || '',
      reference: item.reference || '',
      description: item.description || '',
      quantity: item.quantity || 0,
      unit: item.unit || 'ud',
      unitPrice: item.unitPrice || 0,
      discount: item.discount || 0,
      subtotal: item.subtotal || 0,
      total: item.total || 0,
    })) || []
  );

  // Tax state
  const [taxRate, setTaxRate] = useState(21);

  // Observations
  const [observations, setObservations] = useState('');

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      if (initialDeliveryNote) {
        setCustomer({
          name: initialDeliveryNote.customer.name || '',
          cif: initialDeliveryNote.customer.cif || '',
          address: initialDeliveryNote.customer.address || '',
          city: initialDeliveryNote.customer.city || '',
          province: initialDeliveryNote.customer.province || '',
          postalCode: initialDeliveryNote.customer.postalCode || '',
        });
        setDocumentNumber(initialDeliveryNote.documentNumber);
        setDocumentDate(initialDeliveryNote.date);
        setPaymentMethod(initialDeliveryNote.paymentMethod || 'Transferencia bancaria');
        setHideValues(initialDeliveryNote.hideValues === true);
        setTaxRate(initialDeliveryNote.taxes?.[0]?.type ?? 21);
        setObservations(initialDeliveryNote.observations || '');
        setItems(
          initialDeliveryNote.items.map((item, index) => {
            const { subtotal, total } = calculateItemTotals(
              item.quantity || 0,
              item.unitPrice || 0,
              item.discount || 0
            );
            return {
              id: item.id || `item-${index}`,
              article: item.article || '',
              description: item.description || '',
              reference: item.reference || '',
              quantity: item.quantity || 0,
              unit: item.unit || 'ud',
              unitPrice: item.unitPrice || 0,
              discount: item.discount || 0,
              subtotal: item.subtotal ?? subtotal,
              total: item.total ?? total,
            };
          })
        );
        setNoteId(initialDeliveryNoteId || null);
      } else {
        setCustomer({
          name: initialCustomer?.name || '',
          cif: initialCustomer?.cif || '',
          address: initialCustomer?.address || '',
          city: initialCustomer?.city || '',
          province: initialCustomer?.province || '',
          postalCode: initialCustomer?.postalCode || '',
        });
        setDocumentNumber(generateDocumentNumber(documentSequence));
        setDocumentDate(format(new Date(), 'dd/MM/yyyy'));
        setPaymentMethod('Transferencia bancaria');
        setHideValues(false);
        setTaxRate(21);
        setObservations('');
        setNoteId(null);

        if (initialItems && initialItems.length > 0) {
          setItems(
            initialItems.map((item, index) => {
              const { subtotal, total } = calculateItemTotals(
                item.quantity || 0,
                item.unitPrice || 0,
                item.discount || 0
              );
              return {
                id: `item-${index}`,
                article: item.article || '',
                description: item.description || '',
                reference: item.reference || '',
                quantity: item.quantity || 0,
                unit: item.unit || 'ud',
                unitPrice: item.unitPrice || 0,
                discount: item.discount || 0,
                subtotal,
                total,
              };
            })
          );
        } else {
          setItems([]);
        }
      }
    }
  }, [
    open,
    initialCustomer,
    initialItems,
    initialDeliveryNote,
    initialDeliveryNoteId,
    documentSequence,
  ]);

  // Add new item
  const addItem = () => {
    setItems([
      ...items,
      {
        id: `item-${Date.now()}`,
        article: '',
        reference: '',
        description: '',
        quantity: 0,
        unit: 'ud',
        unitPrice: 0,
        discount: 0,
        subtotal: 0,
        total: 0,
      },
    ]);
  };

  // Remove item
  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  // Update item
  const updateItem = (id: string, field: keyof DeliveryNoteItem, value: any) => {
    setItems(
      items.map(item => {
        if (item.id !== id) return item;

        const updated = { ...item, [field]: value };

        // Recalculate totals when quantity, price, or discount changes
        if (field === 'quantity' || field === 'unitPrice' || field === 'discount') {
          const { subtotal, total } = calculateItemTotals(
            field === 'quantity' ? value : updated.quantity,
            field === 'unitPrice' ? value : updated.unitPrice,
            field === 'discount' ? value : updated.discount
          );
          updated.subtotal = subtotal;
          updated.total = total;
        }

        return updated;
      })
    );
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
    const totalDiscount = items.reduce(
      (sum, item) => sum + (item.subtotal - item.total),
      0
    );
    const taxableBase = subtotal - totalDiscount;
    const tax = calculateTax(taxableBase, taxRate);
    const total = taxableBase + tax.amount;

    return { subtotal, totalDiscount, taxableBase, tax, total };
  };

  const totals = calculateTotals();

  // Build delivery note data
  const buildDeliveryNote = (): DeliveryNote => {
    return {
      documentType: 'Albarán',
      documentNumber,
      page: 1,
      totalPages: 1,
      date: documentDate,
      paymentMethod,
      company: DEFAULT_COMPANY,
      customer,
      items,
      taxes: [totals.tax],
      subtotal: totals.subtotal,
      totalDiscount: totals.totalDiscount,
      totalTax: totals.tax.amount,
      total: totals.total,
      observations,
      workOrderId,
      workOrderNumber,
      hideValues,
    };
  };

  const saveDeliveryNote = async (data: DeliveryNote) => {
    const payload = {
      document_type: data.documentType,
      document_number: data.documentNumber,
      document_date: data.date,
      payment_method: data.paymentMethod,
      company: data.company,
      customer: data.customer,
      taxes: data.taxes,
      subtotal: data.subtotal,
      total_discount: data.totalDiscount,
      total_tax: data.totalTax,
      total: data.total,
      observations: data.observations || null,
      hide_values: data.hideValues === true,
      work_order_id: data.workOrderId || null,
      work_order_number: data.workOrderNumber || null,
    };

    let savedNoteId = noteId;

    if (noteId) {
      const { data: savedNote, error } = await api
        .from('delivery_notes')
        .update(payload)
        .eq('id', noteId)
        .select();

      if (error) throw error;

      const note = Array.isArray(savedNote) ? savedNote[0] : savedNote;
      savedNoteId = note?.id || noteId;
    } else {
      const { data: savedNote, error } = await api
        .from('delivery_notes')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;

      const note = Array.isArray(savedNote) ? savedNote[0] : savedNote;
      savedNoteId = note?.id;
    }

    if (!savedNoteId) {
      throw new Error('No se pudo guardar el albaran');
    }

    const { error: deleteError } = await api
      .from('delivery_note_items')
      .delete()
      .eq('delivery_note_id', savedNoteId);

    if (deleteError) throw deleteError;

    if (data.items.length > 0) {
      const itemPayload = data.items.map((item, index) => ({
        delivery_note_id: savedNoteId,
        line_number: index + 1,
        article: item.article,
        description: item.description,
        reference: item.reference,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unitPrice,
        discount: item.discount,
        subtotal: item.subtotal,
        total: item.total,
      }));

      const { error: itemsError } = await api
        .from('delivery_note_items')
        .insert(itemPayload);

      if (itemsError) throw itemsError;
    }

    if (!noteId) {
      setNoteId(savedNoteId);
    }
    if (onSaved) {
      onSaved(savedNoteId);
    }

    return savedNoteId;
  };

  // Handle download
  const handleDownload = async () => {
    try {
      setLoading(true);
      const data = buildDeliveryNote();
      await saveDeliveryNote(data);
      downloadDeliveryNotePdf(data);
      toast.success('Albarán descargado correctamente');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al guardar o generar el PDF');
    } finally {
      setLoading(false);
    }
  };

  // Handle preview
  const handlePreview = async () => {
    try {
      setLoading(true);
      const data = buildDeliveryNote();
      await saveDeliveryNote(data);
      openDeliveryNotePdfInNewTab(data);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al guardar o generar el PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[95vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Generar Albarán
          </DialogTitle>
          <DialogDescription>
            Edita los datos del albarán antes de generar el PDF
            {workOrderNumber && ` - Orden: ${workOrderNumber}`}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(95vh-180px)] px-6">
          <div className="space-y-6 pb-4">
            {/* Document Info */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Información del Documento</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="docNumber">Número</Label>
                    <Input
                      id="docNumber"
                      value={documentNumber}
                      onChange={e => setDocumentNumber(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="docDate">Fecha</Label>
                    <Input
                      id="docDate"
                      value={documentDate}
                      onChange={e => setDocumentDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="paymentMethod">Forma de Pago</Label>
                    <Input
                      id="paymentMethod"
                      value={paymentMethod}
                      onChange={e => setPaymentMethod(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-3 md:col-span-2">
                    <Switch checked={hideValues} onCheckedChange={setHideValues} />
                    <div className="space-y-0.5">
                      <Label>Albarán de transporte</Label>
                      <p className="text-xs text-muted-foreground">
                        Oculta forma de pago, precio y total en el PDF
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer Info */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Datos del Cliente</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customerName">Nombre / Razón Social *</Label>
                    <Input
                      id="customerName"
                      value={customer.name}
                      onChange={e =>
                        setCustomer({ ...customer, name: e.target.value })
                      }
                      placeholder="Nombre del cliente"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerCif">CIF/NIF *</Label>
                    <Input
                      id="customerCif"
                      value={customer.cif}
                      onChange={e =>
                        setCustomer({ ...customer, cif: e.target.value })
                      }
                      placeholder="B12345678"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="customerAddress">Dirección</Label>
                    <Input
                      id="customerAddress"
                      value={customer.address}
                      onChange={e =>
                        setCustomer({ ...customer, address: e.target.value })
                      }
                      placeholder="Calle, número, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerPostalCode">Código Postal</Label>
                    <Input
                      id="customerPostalCode"
                      value={customer.postalCode}
                      onChange={e =>
                        setCustomer({ ...customer, postalCode: e.target.value })
                      }
                      placeholder="00000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerCity">Ciudad</Label>
                    <Input
                      id="customerCity"
                      value={customer.city}
                      onChange={e =>
                        setCustomer({ ...customer, city: e.target.value })
                      }
                      placeholder="Ciudad"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerProvince">Provincia</Label>
                    <Input
                      id="customerProvince"
                      value={customer.province}
                      onChange={e =>
                        setCustomer({ ...customer, province: e.target.value })
                      }
                      placeholder="Provincia"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Items */}
            <Card>
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Artículos</CardTitle>
                <Button size="sm" variant="outline" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">Artículo</TableHead>
                        <TableHead className="w-24">Referencia</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="w-24">Cantidad</TableHead>
                        <TableHead className="w-24">Precio Ud.</TableHead>
                        <TableHead className="w-20">Dto. %</TableHead>
                        <TableHead className="w-24 text-right">Total</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            className="text-center text-muted-foreground py-8"
                          >
                            No hay artículos. Haz clic en "Añadir" para agregar uno.
                          </TableCell>
                        </TableRow>
                      ) : (
                        items.map(item => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Input
                                value={item.article}
                                onChange={e =>
                                  updateItem(item.id, 'article', e.target.value)
                                }
                                placeholder="Código"
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                            <TableCell>
                              <Input
                                value={item.reference || ""}
                                onChange={e =>
                                  updateItem(item.id, "reference", e.target.value)
                                }
                                placeholder="Ref."
                                className="h-8"
                              />
                            </TableCell>
                              <Textarea
                                value={item.description}
                                onChange={e =>
                                  updateItem(item.id, 'description', e.target.value)
                                }
                                placeholder="Descripción del artículo"
                                className="min-h-[60px] resize-none"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.quantity || ''}
                                onChange={e =>
                                  updateItem(
                                    item.id,
                                    'quantity',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unitPrice || ''}
                                onChange={e =>
                                  updateItem(
                                    item.id,
                                    'unitPrice',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.1"
                                value={item.discount || ''}
                                onChange={e =>
                                  updateItem(
                                    item.id,
                                    'discount',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(item.total)} €
                            </TableCell>
                            <TableCell>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => removeItem(item.id)}
                                className="h-8 w-8 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Tax and Totals */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Impuestos y Totales</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="taxRate">IVA (%)</Label>
                    <Input
                      id="taxRate"
                      type="number"
                      value={taxRate}
                      onChange={e => setTaxRate(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Base Imponible</Label>
                    <div className="h-10 px-3 py-2 bg-muted rounded-md font-medium">
                      {formatCurrency(totals.taxableBase)} €
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>IVA ({taxRate}%)</Label>
                    <div className="h-10 px-3 py-2 bg-muted rounded-md font-medium">
                      {formatCurrency(totals.tax.amount)} €
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>TOTAL</Label>
                    <div className="h-10 px-3 py-2 bg-primary text-primary-foreground rounded-md font-bold text-lg">
                      {formatCurrency(totals.total)} €
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Observations */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Observaciones</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Textarea
                  value={observations}
                  onChange={e => setObservations(e.target.value)}
                  placeholder="Notas adicionales para el albarán..."
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        <Separator />

        <DialogFooter className="px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button variant="outline" onClick={handlePreview} disabled={loading}>
            <Eye className="w-4 h-4 mr-2" />
            Vista Previa
          </Button>
          <Button onClick={handleDownload} disabled={loading}>
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {hideValues ? 'Descargar PDF (sin valores)' : 'Descargar PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
